import { EditDocumentTree, EditDocumentSection } from './edit-document-tree';
import { DocumentNode } from "../document-tree-loader";

export class EditDocumentModal {
    baseRoute: string = "/api/document-client";
    spAuthRoute: string = "/api/document-client/user-sp-auth";

    clientId: string;
    treeLabel: string;
    parentTree: EditDocumentTree;
    modalId: string;
    modal: JQuery<HTMLElement>;

    modalContentBlock: JQuery<HTMLElement>;
    modalSavingContainer: JQuery<HTMLElement>;
    modalSavingBlock: JQuery<HTMLElement>;
    modalSavedBlock: JQuery<HTMLElement>;
    modalCloseBtn: JQuery<HTMLElement>;
    modalCloseText: JQuery<HTMLElement>;

    modalProgressContainer: JQuery<HTMLElement>;
    modalProgressBar: JQuery<HTMLElement>;
    modalProgressText: JQuery<HTMLElement>;
    modalProgressTitle: JQuery<HTMLElement>;

    // File Upload
    filesToUpload: File[];
    spMaxFileSize: number = 200000000; // 200 MB (API Max is 250MB, for larger files, could use multi-part upload but takes too long)
    s3MaxFileSize: number = 2000000000; // 2 GB (API Max is 5TB, but server max is 1GB) - It just takes a long time 

    // Multi-Part File Upload
    largeFileSize: number = 52400000; // 50 MB (Handle as multi-part upload);
    xLargeFileSize: number = 250000000; // 250 MB (Handle as chunked upload -> S3 only)
    chunkSize: number = 52400000; // 50MB (limit the number of total requests to the server)
    currentPartsLoaded: number[][] = [];
    totalFileSize: number = -1;

    // Special Characters (avoid when naming files/folders)
    specialCharacters: RegExp = /[`!@#$%^&*+\=\[\]{};':"\\|,.<>\/?~]/;

    constructor(parentTree: EditDocumentTree) {
        this.modalId = "#edit-documents-modal";
        this.modal = $(this.modalId);
        this.clientId = parentTree.clientId;
        this.treeLabel = parentTree.label;
        this.parentTree = parentTree;

        this.modalContentBlock = this.modal.find(".modal-body");
        this.modalSavingContainer = this.modal.find(".edit-document-saving-container");
        this.modalSavingBlock = this.modalSavingContainer.find(".edit-doc-saving");
        this.modalSavedBlock = this.modalSavingContainer.find(".edit-doc-success");
        this.modalCloseBtn = $(this.modalId).find("#edit-documents-close-btn");
        this.modalCloseText = $(this.modalSavingContainer).find(".edit-doc-long-loading-display");

        this.modalProgressContainer = $(this.modal).find(".edit-doc-progress");
        this.modalProgressBar = this.modalProgressContainer.find(".edit-doc-progress-bar");
        this.modalProgressText = this.modalProgressContainer.find(".edit-doc-progress-text");
        this.modalProgressTitle = this.modalProgressContainer.find(".edit-doc-progress-title");

    }

    openModal(): void {
        // Reset Saving
        this.modalSavedBlock.addClass("d-none");
        this.modalSavingBlock.removeClass('d-none');
        this.modalSavingContainer.addClass("d-none");
        this.modalContentBlock.removeClass('d-none');

        this.updateLoadingText();

        this.modal.modal('show');
    }

    showSaving(isSaving: boolean = true): void {
        if (isSaving) {
            this.modalContentBlock.addClass("d-none");
            this.modalSavingBlock.removeClass('d-none');
            this.modalSavingContainer.removeClass('d-none');
        }
        else {
            this.modalContentBlock.removeClass("d-none");
            this.modalSavingBlock.addClass('d-none');
            this.modalSavingContainer.addClass('d-none');
        }
        this.modalSavedBlock.addClass('d-none');
        this.modalProgressContainer.addClass("d-none");
    }

    showSaved(): void {
        this.modalSavedBlock.removeClass('d-none');
        this.modalSavingContainer.removeClass('d-none');

        this.modalContentBlock.addClass("d-none");
        this.modalSavingBlock.addClass('d-none');
        this.modalProgressContainer.addClass("d-none");
    }

    showModalClose(shouldShow: boolean = true): void {    
        if (shouldShow) {
            this.modalCloseBtn.show();
            this.modalCloseText.show();
        }
        else {
            this.modalCloseBtn.hide();
            this.modalCloseText.hide();
        }
    }

    showProgressBar() {
        this.modalProgressBar.css("backgroundSize", `0%`);
        this.modalProgressText.text(`0%`);
        this.modalSavingContainer.removeClass('d-none');
        this.modalProgressContainer.removeClass("d-none");

        this.modalContentBlock.addClass("d-none");
        this.modalSavingBlock.addClass('d-none');
        this.modalSavedBlock.addClass('d-none');
    }

    updateProgressBarTitle(textVal: string) {
        this.modalProgressTitle.text(textVal);
    }

    // Add New Folder
    initializeAddNewFolder(data: any): void {
        let typeName = "add-folder"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Add New Folder</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div class="form-group">
                    <label for="${this.clientId}-${typeName}">New Folder Name</label>
                    <input type="text" class="form-control" id="${this.clientId}-${typeName}" placeholder="New Folder...">
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-folder-plus pr-2"></i>Add New Folder</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">New Folder Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        // Update Button on input
        $(`#${this.clientId}-${typeName}`).on('keyup change', () => {
            let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();
            let submitButton = $(`#${this.clientId}-${typeName}-submit`);

            if (newFolderName.trim() != "") {
                submitButton.prop('disabled', false);
            }
            else {
                submitButton.prop('disabled', true);
            }

            // Update folder structure with new name
            let updatedStructure = this.generateDisplayStructure(data, typeName, newFolderName);
            $(`#${this.clientId}-${typeName}-structure`).html(updatedStructure);
        })

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = this.validateAddNewFolder(data);

            if (isValid) {
                // Updating Saving Text
                this.updateLoadingText("Adding Folder", "Folder Added!");

                // Show Saving
                this.showSaving();

                let targetNodeId = data?.node?.original?.id;

                // Submit
                let errorMessage = await this.submitAddNewFolder(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetNodeId);
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments(targetNodeId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private validateAddNewFolder(data:any): boolean {
        let typeName = "add-folder"

        // Hide existing errors
        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        let isValid = true;

        let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        // Check for text in input field
        if (newFolderName.trim() == "") {
            errorText.text("Please enter the name of the new folder.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Check for special characters
        if (this.specialCharacters.test(newFolderName)) {
            errorText.text("The new folder name cannot contain special characters.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Make sure the new folder doesn't already exist
        let targetNodeId = data?.node?.original?.id;
        if (!targetNodeId) return false;
        let newFolderPath = `${targetNodeId}/${newFolderName.trim()}`;
        let foundExistingFolders: boolean = this.parentTree.allNodes.some(node => node.id.toLowerCase() == newFolderPath.toLowerCase());
        if (foundExistingFolders) {
            errorText.text("A folder with this name already exists in this location. Please use a different name.");
            errorBlock.removeClass('d-none');
            return false;
        }

        return isValid;
    }

    private async submitAddNewFolder(data: any): Promise<any> {
        let typeName = "add-folder"
        let route = `${this.baseRoute}/add-folder`

        let currentFolderPath = data?.node?.original?.id;

        let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        if (!currentFolderPath || !newFolderName) return;

        // Show Loading in Doc Tree
        this.showDocTreeLoading("Adding New Folder...");

        let requestBody = {
            newFolderName: newFolderName,
            folderPath: currentFolderPath
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"               
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Add New File
    initializeAddNewFile(data: any): void {
        this.filesToUpload = [];

        let typeName = "add-file"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Add New File</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        // User Input
        let userInput = `
            <div class="my-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div id="file-upload-block" class="file-upload-block p-3">
                    <div class="text-center">
                        <i class="fas fa-cloud-upload-alt fa-2x"></i>
                    </div>
                    <p class="text-center">Drag and Drop Files Here!</p>

                    <input id="${this.clientId}-${typeName}" class="position-absolute file-upload-input invisible" type="file" multiple/>

                    <div class="text-center mb-2">
                        <label class="btn btn-sm choose-file-btn" for="${this.clientId}-${typeName}">Choose Files <i class="fa-solid fa-file pl-1"></i></label>
                    </div>

                    <div class="file-upload-gallery"></div>
                </div>
                <div class="mt-3">
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-file-circle-plus pr-2"></i>Add New Files</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">New File Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();
        // disable default behavior for upload blocks
        let uploadBlock = this.modal.find("#file-upload-block");

        uploadBlock.on("dragenter dragover dragleave drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        // disable default behavior for modal
        this.modal.on("dragenter dragover dragleave drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        uploadBlock.on("dragenter dragover", (e) => {
            uploadBlock.addClass("highlight-upload-block");
        })

        uploadBlock.on("dragleave drop", (e) => {
            uploadBlock.removeClass("highlight-upload-block");
        })

        uploadBlock.on("drop", (e) => {
            // Reset Current File (only allowing single file uploads)
           // this.filesToUpload = [];

            let uploadedFiles = e.originalEvent.dataTransfer.files;

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < uploadedFiles.length; i++) {
                if (this.filesToUpload.findIndex(file => file.name == uploadedFiles[i].name) < 0) {
                    this.filesToUpload.push(uploadedFiles[i])
                }
            }

            this.drawFileGallery(data, typeName);

            this.updateAddFileStructure(data, typeName);
        });

        let uploadInput = this.modal.find(`#${this.clientId}-${typeName}`);
        uploadInput.on('change', (e) => {
            //this.filesToUpload = [];
            let uploadedFiles: File[] = uploadInput.prop("files");

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < uploadedFiles.length; i++) {
                if (this.filesToUpload.findIndex(file => file.name == uploadedFiles[i].name) < 0) {
                    this.filesToUpload.push(uploadedFiles[i])
                }
            }

            this.drawFileGallery(data, typeName);

            this.updateAddFileStructure(data, typeName);
        })


        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = await this.validateAddNewFile();

            if (isValid) {
                // Updating Saving Text
                let displayName = "File";
                if (this.filesToUpload.length > 1) displayName = "Files"
                this.updateLoadingText(`Saving ${displayName}`, `${displayName} Saved!`);

                // Show Progress Bar
                this.updateProgressBarTitle(`Uploading ${displayName}`);
                this.showProgressBar();

                let targetNodeId = data?.node?.original?.id;

                // Submit
                let errorMessage = await this.submitAddNewFile(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetNodeId);
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments(targetNodeId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private async validateAddNewFile(): Promise<boolean> {
        let typeName = "add-file"

        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        let isCollab = false;
        for (let i = 0; i < this.filesToUpload.length; i++) {
            let collabCheckId = `#${this.clientId}-add-file-collab-check-${i}`;
            if ($(collabCheckId).is(":checked")) {
                isCollab = true;
            }
        }

        if (this.parentTree.section.isSynced() || isCollab) {
            let validSpToken = await this.validateSPToken();
            if (!validSpToken) {
                errorText.text("Please login to SharePoint to upload this document.");
                errorBlock.removeClass('d-none');
                return false;
            }
        }

        let isValid = true;
        let totalSize = 0;
        if (this.filesToUpload.length > 0) {
            this.filesToUpload.forEach(file => {
                totalSize += file.size;

                // Check File Name for special characters
                let fileName = file.name;
                let fileNameArr = fileName.split(".");
                let fileExtension = fileNameArr.pop();
                fileExtension = `.${fileExtension}`;
                let nameCheck = fileNameArr.join(".");
                if (this.specialCharacters.test(nameCheck)) {
                    errorText.text("The uploaded file name cannot contain special characters. ");
                    errorBlock.removeClass('d-none');
                    isValid = false;
                }

                // Check file size for collaborative documents
                if (this.parentTree.section.isSharePoint() || this.parentTree.section.isMerged()) {
                    if (file.size > this.spMaxFileSize && isCollab) {
                        errorText.text("The uploaded file exceeds the maximum file size for collaborative documents (200 MB).");
                        errorBlock.removeClass('d-none');
                        isValid = false;
                    }
                }
                if (file.size > this.s3MaxFileSize) {
                    errorText.text("The uploaded file exceeds the maximum file size (2 GB).");
                    errorBlock.removeClass('d-none');
                    isValid = false;
                }
            });

            if (totalSize > this.s3MaxFileSize) {
                errorText.text("The uploaded files exceeds the maximum total file size (2 GB).");
                errorBlock.removeClass('d-none');
                isValid = false;
            }

            return isValid;
        }

        errorText.text("Please select a file to upload.");
        errorBlock.removeClass('d-none');

        return false;
    }

    private async submitAddNewFile(data: any): Promise<string> {
        let route = `${this.baseRoute}/add-file`
        let checkTaskRoute = `${this.baseRoute}/check-task`;
        let folderTarget = data?.node?.original?.id;
        let largeFile: boolean = false;
        let xLargeFile: boolean = false;
        let collabFileNameArr: string[] = [];

        if (!folderTarget) {
            return;
        }

        if (this.filesToUpload.some(file => file.size > this.xLargeFileSize)) {
            xLargeFile = true;
        }

        if (xLargeFile) {
            let result = await this.uploadXLargeFiles(folderTarget);
            return result;
        }

        this.showModalClose(false);
        this.showDocTreeLoading("Uploading File...");

        let formData: FormData = new FormData();
        for (let i = 0; i < this.filesToUpload.length; i++) {
            let key: string = folderTarget + "/" + this.filesToUpload[i].name;
            formData.append(key, this.filesToUpload[i]);
            if (this.filesToUpload[i].size > this.largeFileSize) {
                largeFile = true;
            }

            let collabCheckId = `#${this.clientId}-add-file-collab-check-${i}`;
            if ($(collabCheckId).is(":checked")) {
                collabFileNameArr.push(this.filesToUpload[i].name);
            }
        }
        if (largeFile) {
            route = `${this.baseRoute}/add-large-file`
        }

        let taskId: string;
        let result: string;
        try {
            taskId = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId,
                    "FolderPath": folderTarget,
                    "CollabFileNames": collabFileNameArr.join("|")
                },      
                contentType: false,
                cache: false,
                processData: false,
                data: formData,
                xhr: () => {
                    let xhr = new window.XMLHttpRequest();
                    xhr.upload.addEventListener("progress", (e) => {
                        if (e.lengthComputable) {
                            let amountComplete = e.loaded / e.total;
                            let percentComplete = Math.round(amountComplete * 99);

                            this.modalProgressBar.css("backgroundSize", `${percentComplete}%`);
                            this.modalProgressText.text(`${percentComplete}%`);
                        }
                    }, false);
                    xhr.addEventListener("load", (e) => {
                        let percentComplete = 100;
                        this.modalProgressBar.css("backgroundSize", `${percentComplete}%`);
                        this.modalProgressText.text(`${percentComplete}%`);
                    }, false);

                    return xhr;
                }
            });

            this.showSaving();
            this.showModalClose(true);

            while (true) {
                if ($(this.modalId).is(":hidden")) {
                    result = "";
                    break;
                }

                let targetRoute = `${checkTaskRoute}?taskId=${taskId}`;
                let checkTask: string = await $.ajax(targetRoute, {
                    method: "GET",
                    cache: false
                });

                if (checkTask != taskId) {
                    result = checkTask;
                    break;
                }

                await this.delay(1000);
            }

            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
            this.showModalClose(true);
            return null;
        }
    }

    private async uploadXLargeFiles(folderTarget: string): Promise<string> {
        let startRequestRoute = `${this.baseRoute}/start-add-xl-file`;

        this.showModalClose(false);
        this.showDocTreeLoading("Uploading File...");

        //let fileUploadArr: Promise<any>[] = [];

        this.currentPartsLoaded = [];
        this.totalFileSize = 0;
        this.filesToUpload.forEach(file => {
            this.totalFileSize += file.size;
            this.currentPartsLoaded.push([]);
        });

        let uploadIdArr: string[] = [];

        for (let i = 0; i < this.filesToUpload.length; i++) {
            let file: File = this.filesToUpload[i];
            let totalParts = Math.ceil(file.size / this.chunkSize);
            this.currentPartsLoaded[i] = Array(totalParts).fill(0);

            let requestBody = {
                clientId: this.clientId,
                fileName: file.name,
                folderPath: folderTarget,
                totalChunks: totalParts
            }

            let uploadId = await $.ajax(startRequestRoute, {
                method: "POST",
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });

            uploadIdArr.push(uploadId);
            await this.uploadMultiPartFile(file, i, uploadId);
        }

        // Get all of the 'complete' requests ready
        let completeRequests: Promise<any>[] = [];
        for (let i = 0; i < uploadIdArr.length; i++) {
            let uploadId = uploadIdArr[i];

            completeRequests.push(this.completeMultiPartUpload(uploadId));
        }

        this.showSaving();
        this.showModalClose(true);

        // Start all of the complete requests
        let completeResult = await Promise.all(completeRequests);

        return "";
    }

    private async uploadMultiPartFile(file: File, index: number, uploadId: string): Promise<string> {
        let partNum = 0;
        for (let start = 0; start < file.size; start += this.chunkSize) {
            const chunk = file.slice(start, start + this.chunkSize);
            await this.uploadFilePart(uploadId, partNum, chunk, index);
            partNum++;
        }

        return "";
    }

    private async uploadFilePart(uploadId: string, partNum: number, chunk: Blob, index: number): Promise<string> {
        let uploadPartRoute = `${this.baseRoute}/add-xl-file-part`;
        const formData = new FormData();
        formData.set('data', chunk);

        let errorMessage = "";

        let retryCount = 0;
        let maxCount = 5;
        let isUploaded = false;

        while (!isUploaded) {
            try {
                let result = await $.ajax(uploadPartRoute, {
                    method: "POST",
                    headers: {
                        "UploadId": uploadId,
                        "UploadPartNum": partNum.toString(),
                    },
                    contentType: false,
                    cache: false,
                    processData: false,
                    data: formData,
                    xhr: () => {
                        let xhr = new window.XMLHttpRequest();
                        xhr.upload.addEventListener("progress", (e) => {
                            if (e.lengthComputable) {
                                this.updateMultiPartProgressBar(index, partNum, e.loaded);
                            }
                        }, false);

                        return xhr;
                    }
                });

                isUploaded = true;
                break;
            }
            catch (e) {
                console.log(e);
            }

            if (retryCount < maxCount) {
                // Delay 5 seconds before retrying
                retryCount += 1;
                this.updateMultiPartProgressBar(index, partNum, 0);
                console.log(`Retrying Request: ${uploadId}-${partNum}`);
                await this.delay(5000);
            }
            else {
                isUploaded = true;
                errorMessage = "Retry Count Exceeded";
                console.log(errorMessage);
                break;
            }
        }

        return errorMessage;
    }

    private async completeMultiPartUpload(uploadId: string): Promise<any> {
        let completeRequestRoute = `${this.baseRoute}/complete-add-xl-file`;
        let checkTaskRoute = `${this.baseRoute}/check-task`;

        let requestBody = {
            clientId: this.clientId,
            uploadId: uploadId
        }

        let result: string;
        let taskId: string;

        try {
            taskId = await $.ajax(completeRequestRoute, {
                method: "POST",
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });

            while (true) {
                if ($(this.modalId).is(":hidden")) {
                    result = "";
                    break;
                }

                try {

                    let targetRoute = `${checkTaskRoute}?taskId=${taskId}`;
                    let checkTask: string = await $.ajax(targetRoute, {
                        method: "GET",
                        cache: false
                    });

                    if (checkTask != taskId) {
                        result = checkTask;
                        break;
                    }
                }
                catch (e) {
                    console.log(e);
                }
                
                await this.delay(5000);
            }
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }

        return result;
    }

    private async updateMultiPartProgressBar(fileIndex: number, partNum: number, loadedAmount: number) {
        this.currentPartsLoaded[fileIndex][partNum] = loadedAmount;

        let totalLoaded = 0;
        this.currentPartsLoaded.forEach(fileArr => {
            fileArr.forEach(chunkLoad => {
                totalLoaded += chunkLoad;
            })
        })

        let amountComplete = totalLoaded / this.totalFileSize;
        let percentComplete = Math.round(amountComplete * 99);

        this.modalProgressBar.css("backgroundSize", `${percentComplete}%`);
        this.modalProgressText.text(`${percentComplete}%`);
    }


    // Rename Folder
    initalizeRenameFolder(data: any): void {
        let typeName = "rename-folder"

        // Header
        let header = `
            <div class="edit-doc-header mb-2">Rename Folder</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        let placeholder = data?.node?.original?.text;
        if (!placeholder) placeholder = "New Folder Name..."

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div class="form-group">
                    <label for="${this.clientId}-${typeName}">New Folder Name</label>
                    <div class="input-group">
                        <input type="text" class="form-control" id="${this.clientId}-${typeName}" placeholder="${placeholder}">
                    </div>
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-pen-to-square pr-2"></i>Rename Folder</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">Folder Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        // Update Button on input
        $(`#${this.clientId}-${typeName}`).on('keyup change', () => {
            let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();
            let submitButton = $(`#${this.clientId}-${typeName}-submit`);

            if (newFolderName.trim() != "") {
                submitButton.prop('disabled', false);
            }
            else {
                submitButton.prop('disabled', true);
            }

            // Update folder structure with new name
            let updatedStructure = this.generateDisplayStructure(data, typeName, newFolderName);
            $(`#${this.clientId}-${typeName}-structure`).html(updatedStructure);
        })

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = this.validateRenameFolder(data);

            if (isValid) {
                // Updating Saving Text
                this.updateLoadingText("Renaming Folder", "Folder Name Updated!");

                // Show Saving
                this.showSaving();

                let targetNodeId = data?.node?.original?.parent;

                // Submit
                let errorMessage = await this.submitRenameFolder(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetNodeId);
                    return;
                }

                // Refresh Documents     
                this.parentTree.refreshDocuments(targetNodeId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private validateRenameFolder(data: any): boolean {
        let typeName = "rename-folder"

        // Hide existing errors
        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text")

        let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();


        // Check for text in input field
        if (newFolderName?.trim() == "" || !newFolderName) {
            errorText.text("Please enter a folder name.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Check for special characters
        if (this.specialCharacters.test(newFolderName)) {
            errorText.text("The new folder name cannot contain special characters.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Add Check for ensuring that new folder does not overwrite existing folder
        let parent = data?.node?.original?.parent;
        let newFolderPath = `${parent}/${newFolderName.trim()}`
        if (parent == null || parent == undefined || parent == "#") {
            newFolderPath = newFolderName.trim();
        }
        let foundExistingFolder = this.parentTree.allNodes.some(node => node.id.toLowerCase() == newFolderPath.toLowerCase()
            && node.id != data?.node?.original?.id);
        if (foundExistingFolder) {
            errorText.text("A folder with this name already exists in this location. Please use a different name.")
            errorBlock.removeClass('d-none');
            return false;
        }

        return true;
    }

    private async submitRenameFolder(data: any): Promise<any> {
        let typeName = "rename-folder"
        let route = `${this.baseRoute}/rename-folder`

        let currentFolderPath = data?.node?.original?.id;
        let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        if (!currentFolderPath || !newFolderName) return;

        this.showDocTreeLoading("Renaming Folder...");

        let requestBody = {
            updatedFolderName: newFolderName, 
            folderPath: currentFolderPath
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Rename File
    initializeRenameFile(data: any): void {
        let typeName = "rename-file"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Rename File</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        let filename = data?.node?.original?.text;
        let fileExtension = "";
        if (filename) {
            let fileArr = filename.split(".");
            let extension = fileArr.pop();
            fileExtension = `.${extension}`;
            filename = fileArr.join(".");
        }

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div class="form-group">
                    <label for="${this.clientId}-${typeName}">New File Name</label>
                    <div class="input-group">
                        <input type="text" class="form-control" id="${this.clientId}-${typeName}" placeholder="${filename}">
                        <div class="input-group-append">
                            <span class="input-group-text">${fileExtension}</span>
                        </div>
                    </div>
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-pen-to-square pr-2"></i>Rename File</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">File Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        // Update Button on input
        $(`#${this.clientId}-${typeName}`).on('keyup change', () => {
            let newFileName: string = <string>$(`#${this.clientId}-${typeName}`).val();
            let submitButton = $(`#${this.clientId}-${typeName}-submit`);

            if (newFileName.trim() != "") {
                submitButton.prop('disabled', false);
            }
            else {
                submitButton.prop('disabled', true);
            }

            // Update folder structure with new name
            let updatedStructure = this.generateDisplayStructure(data, typeName, newFileName);
            $(`#${this.clientId}-${typeName}-structure`).html(updatedStructure);
        })

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = this.validateRenameFile(data);

            if (isValid) {
                // Updating Saving Text
                this.updateLoadingText("Renaming File", "File Name Updated!");

                // Show Saving
                this.showSaving();

                let targetNodeId = data?.node?.original?.parent;

                // Submit
                let errorMessage = await this.submitRenameFile(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetNodeId);
                    return;
                }

                // Refresh Documents                
                this.parentTree.refreshDocuments(targetNodeId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private validateRenameFile(data: any): boolean {
        let typeName = "rename-file";

        // Hide existing errors
        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        let isValid = true;

        let newFileName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        // Check for text in input field
        if (newFileName?.trim() == "" || !newFileName) {
            errorText.text("Please enter a file name.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Check for special characters
        if (this.specialCharacters.test(newFileName)) {
            errorText.text("The new file name cannot contain special characters.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Check if file with this name already exists
        let filename = data?.node?.original?.text;
        let fileArr = filename.split(".");
        let extension = fileArr.pop();

        let currentParent = data?.node?.original?.parent;
        let newFilePath = `${currentParent}/${newFileName.trim()}.${extension}`
        if (currentParent == "#") {
            newFilePath = `${newFileName.trim()}.${extension}`;
        }
        let foundExistingFile = this.parentTree.allNodes.some(node => node.id.toLowerCase() == newFilePath.toLowerCase() &&
            node.id != data?.node?.original?.id)
        if (foundExistingFile) {
            errorText.text("A file with this name already exists in this location. Please use a different name.")
            errorBlock.removeClass('d-none');
            return false;
        }

        return isValid;
    }

    private async submitRenameFile(data: any): Promise<any> {
        let typeName = "rename-file"
        let route = `${this.baseRoute}/rename-file`

        let currentFilePath = data?.node?.original?.id;
        if (!currentFilePath) return;

        let filePathArr = currentFilePath.split("/");
        let currentFileName = filePathArr.pop();
        let currentFolderPath = filePathArr.join("/");

        let newFileName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        if (!newFileName || !currentFileName) return;

        this.showDocTreeLoading("Renaming File...");

        let isCollab = false;
        if (data?.node?.original?.displaySpLink == true) {
            isCollab = true;
        }

        let requestBody = {
            updatedFileName: newFileName,
            folderPath: currentFolderPath,
            fileName: currentFileName,
            isCollab: isCollab
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Delete Folder
    initializeDeleteFolder(data: any): void {
        let typeName = "delete-folder";

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Delete Folder</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        let currentName = data?.node?.original?.text;
        if (!currentName) currentName = "Folder Name"

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div>
                    <label class="mb-0">Confirm Delete</label>
                    <p>Are you sure you want to delete this folder? This folder and all files and folders stored inside of this folder will be removed.</p>
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn edit-doc-danger" id="${this.clientId}-${typeName}-submit"><i class="fa-solid fa-trash-can pr-2"></i>Delete Folder</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">Folder Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName, currentName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();


        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Updating Saving Text
            this.updateLoadingText("Deleting Folder", "Folder Deleted!");

            // Show Saving
            this.showSaving();

            let targetNodeId = data?.node?.original?.parent;

            // Submit
            let errorMessage = await this.submitDeleteFolder(data);
            if (errorMessage) {
                let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                let errorText = errorBlock.find(".error-text");

                errorText.text(errorMessage);
                errorBlock.removeClass('d-none');

                this.showSaving(false);
                this.parentTree.refreshDocuments(targetNodeId);
                return;
            }

            // Refresh Documents          
            this.parentTree.refreshDocuments(targetNodeId);

            // Show Complete
            this.showSaved();

            // Close Modal
            setTimeout(() => {
                this.modal.modal('hide');
            }, 1500);
        })
    }

    private async submitDeleteFolder(data: any): Promise<any> {
        let typeName = "delete-folder"
        let route = `${this.baseRoute}/delete-folder`

        let currentFolderPath = data?.node?.original?.id;

        if (!currentFolderPath) return;

        this.showDocTreeLoading("Deleting Folder...");

        let requestBody = {
            folderPath: currentFolderPath
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Delete File
    initializeDeleteFile(data: any): void {
        let typeName = "delete-file"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Delete File</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        let currentName = data?.node?.original?.text;
        if (!currentName) currentName = "File Name"

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div>
                    <label class="mb-0">Confirm Delete</label>
                    <p>Are you sure you want to delete this file? This file will be permanently removed.</p>
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn edit-doc-danger" id="${this.clientId}-${typeName}-submit"><i class="fa-solid fa-trash-can pr-2"></i>Delete File</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">File Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName, currentName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();


        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Updating Saving Text
            this.updateLoadingText("Deleting File", "File Deleted!");

            // Show Saving
            this.showSaving();

            let targetNodeId = data?.node?.original?.parent;

            // Submit
            let errorMessage = await this.submitDeleteFile(data);
            if (errorMessage) {
                let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                let errorText = errorBlock.find(".error-text");

                errorText.text(errorMessage);
                errorBlock.removeClass('d-none');

                this.showSaving(false);
                this.parentTree.refreshDocuments(targetNodeId);
                return;
            }

            // Refresh Documents
            this.parentTree.refreshDocuments(targetNodeId);

            // Show Complete
            this.showSaved();

            // Close Modal
            setTimeout(() => {
                this.modal.modal('hide')
            }, 1500);
        })
    }

    private async submitDeleteFile(data: any): Promise<any> {
        let typeName = "delete-file"
        let route = `${this.baseRoute}/delete-file`

        let currentFilePath = data?.node?.original?.id;
        if (!currentFilePath) return;

        let filePathArr = currentFilePath.split("/");
        let currentFileName = filePathArr.pop();
        let currentFolderPath = filePathArr.join("/");

        if (!currentFileName) return;
      
        this.showDocTreeLoading("Deleting File...");

        let isCollab = false;
        if (data?.node?.original?.displaySpLink == true) {
            isCollab = true;
        }

        let requestBody = {
            fileName: currentFileName,
            folderPath: currentFolderPath,
            isCollab: isCollab
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Move Folder 
    initializeMoveFolder(data: any, folderNodes: DocumentNode[]): void {
        let typeName = "move-folder";

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Move Folder</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        // User Input
        // Get Parent of Element
        let currentParentId = data?.node?.original?.parent;
        if (!currentParentId) currentParentId = "#";
        // Go one level higher
        let foundParentId = folderNodes.find(node => node.id == currentParentId)?.parent;
        if (!foundParentId) foundParentId = "#";

        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div class="mb-3" id="${this.clientId}-${typeName}-select-container">
                    ${this.drawFolderSelect(data, typeName, folderNodes, foundParentId, currentParentId)}
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-folder-tree pr-2"></i></i>Move Folder</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">New Folder Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `

        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        $(`#${this.clientId}-${typeName}-select-container`).on('click', ".edit-doc-folder-select-item", (e) => {
            // Get New Target Folder
            let targetFolderId = $(e.currentTarget).data("folderId");

            // Update folder select display
            let updatedFolderSelect = this.drawFolderSelect(data, typeName, folderNodes, targetFolderId);
            $(`#${this.clientId}-${typeName}-select-container`).empty().html(updatedFolderSelect);

            // Disable Move Button (nothing selected)
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);

            // Update display structure
            let updatedDisplayStructure = this.generateDisplayStructure(data, typeName);
            $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
        })

        $(`#${this.clientId}-${typeName}-select-container`).on("click", ".folder-check", (e) => {
            // Check if box is checked or not
            let isChecked = $(e.currentTarget).is(':checked');
            if (isChecked) {
                // Remove checks from any other checkboxes
                let targetCheckBox = $(e.currentTarget);

                let otherCheckBoxes = $(`#${this.clientId}-${typeName}-select-container`).find(".folder-check").not(targetCheckBox);
                otherCheckBoxes.prop('checked', false);

                // Mark selected row as selected
                $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row").removeClass("selected-folder-row");
                targetCheckBox.closest(".edit-folder-row").addClass("selected-folder-row");

                // Remove 'disabled' from submit btn
                $(`#${this.clientId}-${typeName}-submit`).prop('disabled', false);


                // Update display structure
                let newFolderPath: string = <string>targetCheckBox.val();
                let updatedDisplayStructure = this.generateDisplayStructure(data, typeName, "", newFolderPath);
                $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
            }
            else {
                // Disabled submit btn
                $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);

                // Remove any selected rows
                $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row").removeClass("selected-folder-row");

                // Update display structure
                let updatedDisplayStructure = this.generateDisplayStructure(data, typeName);
                $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
            }
        })

        $(`#${this.clientId}-${typeName}-select-container`).on("click", ".folder-select", (e) => {
            // Get new folder target
            let targetFolderId = $(e.currentTarget).data("folderId");

            // Update folder select display
            let updatedFolderSelect = this.drawFolderSelect(data, typeName, folderNodes, targetFolderId);
            $(`#${this.clientId}-${typeName}-select-container`).empty().html(updatedFolderSelect);

            // Disable Move Button (nothing selected)
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);

            // Update display structure
            let updatedDisplayStructure = this.generateDisplayStructure(data, typeName);
            $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
        })

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = this.validateMoveFolder(data);

            if (isValid) {
                // Get Updated Folder Path
                let targetRow = $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row");
                let targetCheck = targetRow.find(".folder-check:checked");
                let targetFolderId = <string>targetCheck.val();

                // Updating Saving Text
                this.updateLoadingText("Moving Folder", "Folder Moved!");

                // Show Saving
                this.showSaving();

                // Submit
                let errorMessage = await this.submitMoveFolder(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetFolderId);
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments(targetFolderId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private validateMoveFolder(data: any): boolean {
        let typeName = "move-folder";

        // Hide existing errors
        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        // Check to see if there is already a folder present in target destination
        let targetRow = $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row");
        let targetCheck = targetRow.find(".folder-check:checked");
        let targetFolderId = <string>targetCheck.val();

        let folderName = data?.node?.original?.text;
        let newFolderPath = `${targetFolderId}/${folderName}`;
        if (targetFolderId == "#") {
            newFolderPath = folderName;
        }

        let foundExistingFolder = this.parentTree.allNodes.some(node => node.id.toLowerCase() == newFolderPath.toLowerCase());
        if (foundExistingFolder) {
            errorText.text("A folder with this name already exists in the new location. Please select a different location");
            errorBlock.removeClass("d-none");
            return false;
        }

        return true;
    }

    private async submitMoveFolder(data: any): Promise<any> {
        let typeName = "move-folder"
        let route = `${this.baseRoute}/move-folder`

        // Get Updated Folder Path
        let targetRow = $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row");
        let targetCheck = targetRow.find(".folder-check:checked");
        let targetFolderId = <string>targetCheck.val();

        let currentFolderPath = data?.node?.original?.id;

        if (!currentFolderPath || !targetFolderId) return;

        if (targetFolderId == "#") {
            targetFolderId = "";
        }

        this.showDocTreeLoading("Moving Folder...");

        let requestBody = {
            updatedFolderPath: targetFolderId,
            folderPath: currentFolderPath
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Move File
    initializeMoveFile(data: any, folderNodes: DocumentNode[]): void {
        let typeName = "move-file";

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Move File</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        // User Input
        // Get Parent of Element
        let currentParentId = data?.node?.original?.parent;
        if (!currentParentId) currentParentId = "#";
        // Go one level higher
        let foundParentId = folderNodes.find(node => node.id == currentParentId)?.parent;
        if (!foundParentId) foundParentId = "#";

        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div class="mb-3" id="${this.clientId}-${typeName}-select-container">
                    ${this.drawFolderSelect(data, typeName, folderNodes, foundParentId, currentParentId)}
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-file-export pr-2"></i></i>Move File</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">New File Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `

        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        $(`#${this.clientId}-${typeName}-select-container`).on('click', ".edit-doc-folder-select-item", (e) => {
            // Get New Target Folder
            let targetFolderId = $(e.currentTarget).data("folderId");

            // Update folder select display
            let updatedFolderSelect = this.drawFolderSelect(data, typeName, folderNodes, targetFolderId);
            $(`#${this.clientId}-${typeName}-select-container`).empty().html(updatedFolderSelect);

            // Disable Move Button (nothing selected)
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);

            // Update display structure
            let updatedDisplayStructure = this.generateDisplayStructure(data, typeName);
            $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
        })

        $(`#${this.clientId}-${typeName}-select-container`).on("click", ".folder-check", (e) => {
            // Check if box is checked or not
            let isChecked = $(e.currentTarget).is(':checked');
            if (isChecked) {
                // Remove checks from any other checkboxes
                let targetCheckBox = $(e.currentTarget);

                let otherCheckBoxes = $(`#${this.clientId}-${typeName}-select-container`).find(".folder-check").not(targetCheckBox);
                otherCheckBoxes.prop('checked', false);

                // Mark selected row as selected
                $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row").removeClass("selected-folder-row");
                targetCheckBox.closest(".edit-folder-row").addClass("selected-folder-row");

                // Remove 'disabled' from submit btn
                $(`#${this.clientId}-${typeName}-submit`).prop('disabled', false);


                // Update display structure
                let newFolderPath: string = <string>targetCheckBox.val();
                let updatedDisplayStructure = this.generateDisplayStructure(data, typeName, "", newFolderPath);
                $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
            }
            else {
                // Disabled submit btn
                $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);

                // Remove any selected rows
                $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row").removeClass("selected-folder-row");

                // Update display structure
                let updatedDisplayStructure = this.generateDisplayStructure(data, typeName);
                $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
            }
        })

        $(`#${this.clientId}-${typeName}-select-container`).on("click", ".folder-select", (e) => {
            // Get new folder target
            let targetFolderId = $(e.currentTarget).data("folderId");

            // Update folder select display
            let updatedFolderSelect = this.drawFolderSelect(data, typeName, folderNodes, targetFolderId);
            $(`#${this.clientId}-${typeName}-select-container`).empty().html(updatedFolderSelect);

            // Disable Move Button (nothing selected)
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);

            // Update display structure
            let updatedDisplayStructure = this.generateDisplayStructure(data, typeName);
            $(`#${this.clientId}-${typeName}-structure`).empty().html(updatedDisplayStructure);
        })

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = this.validateMoveFile(data);

            if (isValid) {
                // Get Updated Folder Path
                let targetRow = $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row");
                let targetCheck = targetRow.find(".folder-check:checked");
                let targetFolderId = <string>targetCheck.val();

                // Updating Saving Text
                this.updateLoadingText("Moving File", "File Moved!");

                // Show Saving
                this.showSaving();

                // Submit
                let errorMessage = await this.submitMoveFile(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetFolderId);
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments(targetFolderId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private validateMoveFile(data: any): boolean {
        let typeName = "move-file";

        // Hide existing errors
        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        // Check to see if there is already a folder present in target destination
        let targetRow = $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row");
        let targetCheck = targetRow.find(".folder-check:checked");
        let targetFolderId = <string>targetCheck.val();

        let fileName = data?.node?.original?.text;
        let newFilePath = `${targetFolderId}/${fileName}`;
        if (targetFolderId == "#") {
            newFilePath = fileName;
        }

        let foundExistingFile = this.parentTree.allNodes.some(node => node.id.toLowerCase() == newFilePath.toLowerCase());
        if (foundExistingFile) {
            errorText.text("A file with this name already exists in the new location. Please select a different location");
            errorBlock.removeClass("d-none");
            return false;
        }

        return true;
    }

    private async submitMoveFile(data: any): Promise<any> {
        let typeName = "move-file"
        let route = `${this.baseRoute}/move-file`

        // Get Updated Folder Path
        let targetRow = $(`#${this.clientId}-${typeName}-select-container`).find(".selected-folder-row");
        let targetCheck = targetRow.find(".folder-check:checked");
        let targetFolderId = <string>targetCheck.val();

        let currentFilePath = data?.node?.original?.id;
        let filePathArr = currentFilePath.split("/");
        let currentFileName = filePathArr.pop();
        let currentFolderPath = filePathArr.join("/");

        if (!currentFilePath || !targetFolderId) return;
        if (targetFolderId == "#") {
            targetFolderId = "";
        }

        let isCollab = false;
        if (data?.node?.original?.displaySpLink == true) {
            isCollab = true;
        }

        this.showDocTreeLoading("Moving File...");

        let requestBody = {
            updatedFolderPath: targetFolderId,
            folderPath: currentFolderPath,
            fileName: currentFileName,
            isCollab: isCollab
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Add Folder Root
    initializeAddNewRootFolder(): void {
        let typeName = "add-root-folder"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Add New Folder</div>
        `

        // File Path
        let displayPath = `
            <div class='edit-doc-display-path'>
                <span class='file-path-item'>${this.treeLabel}</span>
            </div>
        `

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div class="form-group">
                    <label for="${this.clientId}-${typeName}">New Folder Name</label>
                    <input type="text" class="form-control" id="${this.clientId}-${typeName}" placeholder="New Folder...">
                </div>
                <div>
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-folder-plus pr-2"></i>Add New Folder</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">New Folder Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(null, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        // Update Button on input
        $(`#${this.clientId}-${typeName}`).on('keyup change', () => {
            let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();
            let submitButton = $(`#${this.clientId}-${typeName}-submit`);

            if (newFolderName.trim() != "") {
                submitButton.prop('disabled', false);
            }
            else {
                submitButton.prop('disabled', true);
            }

            // Update folder structure with new name
            let updatedStructure = this.generateDisplayStructure(null, typeName, newFolderName);
            $(`#${this.clientId}-${typeName}-structure`).html(updatedStructure);
        })

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = this.validateAddNewRootFolder();

            if (isValid) {
                // Updating Saving Text
                this.updateLoadingText("Adding Folder", "Folder Added!");

                // Show Saving
                this.showSaving();

                // Submit
                let errorMessage = await this.submitAddNewRootFolder();
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments();
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments();

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private validateAddNewRootFolder(): boolean {
        let typeName = "add-root-folder"

        // Hide existing errors
        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        let isValid = true;

        let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        // Check for text in input field
        if (newFolderName.trim() == "") {
            errorText.text("Please enter the name of the new folder.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Check for special characters
        if (this.specialCharacters.test(newFolderName)) {
            errorText.text("The new folder name cannot contain special characters.")
            errorBlock.removeClass('d-none');
            return false;
        }

        // Check if folder already exists
        let foundExistingFolder = this.parentTree.allNodes.some(node => node.id.toLowerCase() == newFolderName.trim().toLowerCase());
        if (foundExistingFolder) {
            errorText.text("A folder with this name already exists in this location. Please use a different name.")
            errorBlock.removeClass('d-none');
            return false;
        }

        return isValid;
    }

    private async submitAddNewRootFolder(): Promise<any> {
        let typeName = "add-root-folder"
        let route = `${this.baseRoute}/add-folder`

        let currentFolderPath = "";

        let newFolderName: string = <string>$(`#${this.clientId}-${typeName}`).val();

        if (!newFolderName) return;

        let requestBody = {
            newFolderName: newFolderName,
            folderPath: currentFolderPath
        }

        this.showDocTreeLoading("Adding New Folder...");

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"

            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Add File Root
    initializeAddNewRootFile(): void {
        this.filesToUpload = [];
        let typeName = "add-root-file"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Add New File</div>
        `

        // File Path
        let displayPath = `
            <div class='edit-doc-display-path'>
                <span class='file-path-item'>${this.treeLabel}</span>
            </div>
        `

        // User Input
        let userInput = `
            <div class="my-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div id="file-upload-block" class="file-upload-block p-3">
                    <div class="text-center">
                        <i class="fas fa-cloud-upload-alt fa-2x"></i>
                    </div>
                    <p class="text-center">Drag and Drop Files Here!</p>

                    <input id="${this.clientId}-${typeName}" class="position-absolute file-upload-input invisible" type="file" multiple/>

                    <div class="text-center mb-2">
                        <label class="btn btn-sm choose-file-btn" for="${this.clientId}-${typeName}">Choose Files <i class="fa-solid fa-file pl-1"></i></label>
                    </div>

                    <div class="file-upload-gallery"></div>
                </div>
                <div class="mt-3">
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit" disabled><i class="fa-solid fa-file-circle-plus pr-2"></i>Add New Files</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">New File Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(null, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        // disable default behavior for upload blocks
        let uploadBlock = this.modal.find("#file-upload-block");

        uploadBlock.on("dragenter dragover dragleave drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        // disable default behavior for modal
        this.modal.on("dragenter dragover dragleave drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        uploadBlock.on("dragenter dragover", (e) => {
            uploadBlock.addClass("highlight-upload-block");
        })

        uploadBlock.on("dragleave drop", (e) => {
            uploadBlock.removeClass("highlight-upload-block");
        })

        uploadBlock.on("drop", (e) => {
            // Reset Current File (only allowing single file uploads)
            this.filesToUpload = [];

            let uploadedFiles = e.originalEvent.dataTransfer.files;

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < uploadedFiles.length; i++) {
                if (this.filesToUpload.findIndex(file => file.name == uploadedFiles[i].name) < 0) {
                    this.filesToUpload.push(uploadedFiles[i])
                }
            }

            this.drawFileGallery(null, typeName);
            this.updateAddFileStructure(null, typeName);
        });

        let uploadInput = this.modal.find(`#${this.clientId}-${typeName}`);
        uploadInput.on('change', (e) => {
            this.filesToUpload = [];
            let uploadedFiles: File[] = uploadInput.prop("files");

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < uploadedFiles.length; i++) {
                if (this.filesToUpload.findIndex(file => file.name == uploadedFiles[i].name) < 0) {
                    this.filesToUpload.push(uploadedFiles[i])
                }
            }

            this.drawFileGallery(null, typeName);
            this.updateAddFileStructure(null, typeName);
        })


        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = await this.validateAddNewRootFile();

            if (isValid) {
                // Updating Saving Text
                let displayName = "File";
                if (this.filesToUpload.length > 1) displayName = "Files"
                this.updateLoadingText(`Saving ${displayName}`, `${displayName} Saved!`);

                // Show Saving
                this.updateProgressBarTitle(`Uploading ${displayName}`);
                this.showProgressBar();

                // Submit
                let errorMessage = await this.submitAddNewRootFile();
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments();
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments();

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }

    private async validateAddNewRootFile(): Promise<boolean> {
        let typeName = "add-root-file"

        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        let isCollab = false;
        for (let i = 0; i < this.filesToUpload.length; i++) {
            let collabCheckId = `#${this.clientId}-add-file-collab-check-${i}`;
            if ($(collabCheckId).is(":checked")) {
                isCollab = true;
            }
        }

        if (this.parentTree.section.isSynced() || isCollab) {
            let validSpToken = await this.validateSPToken();
            if (!validSpToken) {
                errorText.text("Please login to SharePoint to upload this document.");
                errorBlock.removeClass('d-none');
                return false;
            }
        }

        let isValid = true;
        let totalSize = 0;
        if (this.filesToUpload.length > 0) {
            this.filesToUpload.forEach(file => {
                totalSize += file.size;
                // Check File Name for special characters
                let fileName = file.name;
                let fileNameArr = fileName.split(".");
                let fileExtension = fileNameArr.pop();
                fileExtension = `.${fileExtension}`;
                let nameCheck = fileNameArr.join(".");
                if (this.specialCharacters.test(nameCheck)) {
                    errorText.text("The uploaded file name cannot contain special characters. ");
                    errorBlock.removeClass('d-none');
                    isValid = false;
                }

                // Check file size for collaborative documents
                if (this.parentTree.section.isSharePoint() || this.parentTree.section.isMerged()) {
                    if (file.size > this.spMaxFileSize && isCollab) {
                        errorText.text("The uploaded file exceeds the maximum file size for collaborative documents (200 MB).");
                        errorBlock.removeClass('d-none');
                        isValid = false;
                    }
                }
                if (file.size > this.s3MaxFileSize) {
                    errorText.text("The uploaded file exceeds the maximum file size (1 GB).");
                    errorBlock.removeClass('d-none');
                    isValid = false;
                }
            });

            if (totalSize > this.s3MaxFileSize) {
                errorText.text("The uploaded files exceeds the maximum total file size (1 GB).");
                errorBlock.removeClass('d-none');
                isValid = false;
            }

            return isValid;
        }

        // We might want to check for file size here
        errorText.text("Please select a file to upload.");
        errorBlock.removeClass('d-none');

        return false;
    }

    private async submitAddNewRootFile(): Promise<any> {
        let route = `${this.baseRoute}/add-file`
        let checkTaskRoute = `${this.baseRoute}/check-task`;
        let folderTarget = "";
        let largeFile: boolean = false;
        let xLargeFile: boolean = false;
        let collabFileNameArr: string[] = [];

        if (this.filesToUpload.some(file => file.size > this.xLargeFileSize)) {
            xLargeFile = true;
        }

        if (xLargeFile) {
            let result = await this.uploadXLargeFiles(folderTarget);
            return result;
        }

        this.showModalClose(false);
        this.showDocTreeLoading("Uploading File...");

        let formData: FormData = new FormData();
        for (let i = 0; i < this.filesToUpload.length; i++) {
            let key: string = folderTarget + "/" + this.filesToUpload[i].name;
            formData.append(key, this.filesToUpload[i]);
            if (this.filesToUpload[i].size > this.largeFileSize) {
                largeFile = true;
            }

            let collabCheckId = `#${this.clientId}-add-file-collab-check-${i}`;
            if ($(collabCheckId).is(":checked")) {
                collabFileNameArr.push(this.filesToUpload[i].name);
            }
        }
        if (largeFile) {
            route = `${this.baseRoute}/add-large-file`
        }

        let taskId: string;
        let result: string;
        try {
            taskId = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId,
                    "FolderPath": folderTarget,
                    "CollabFileNames": collabFileNameArr.join("|")
                },
                contentType: false,
                cache: false,
                processData: false,
                data: formData,
                xhr: () => {
                    let xhr = new window.XMLHttpRequest();
                    xhr.upload.addEventListener("progress", (e) => {
                        if (e.lengthComputable) {
                            let amountComplete = e.loaded / e.total;
                            let percentComplete = Math.round(amountComplete * 99);

                            this.modalProgressBar.css("backgroundSize", `${percentComplete}%`);
                            this.modalProgressText.text(`${percentComplete}%`);
                        }
                    }, false);
                    xhr.addEventListener("load", (e) => {
                        let percentComplete = 100;
                        this.modalProgressBar.css("backgroundSize", `${percentComplete}%`);
                        this.modalProgressText.text(`${percentComplete}%`);
                    }, false);

                    return xhr;
                }
            });

            this.showSaving();
            this.showModalClose(true);

            while (true) {
                if ($(this.modalId).is(":hidden")) {
                    result = "";
                    break;
                }

                let targetRoute = `${checkTaskRoute}?taskId=${taskId}`;
                let checkTask: string = await $.ajax(targetRoute, {
                    method: "GET",
                    cache: false
                });

                if (checkTask != taskId) {
                    result = checkTask;
                    break;
                }

                await this.delay(1000);
            }

            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
            this.showModalClose(true);
            return null;
        }
    }

    // Collaborative Document Options
    iniializeCollabDocUpdate(data: any): void {
        let typeName = "collab-doc-update"

        // Header
        let header = `
            <div class="edit-doc-header mb-1">Collaborative Options</div>
        `

        // File Path
        let displayPath = this.generateDisplayPath(data);

        let currentCollabCheck = "";
        if (data?.node?.original?.displaySpLink == true) {
            currentCollabCheck = "checked";
        }

        // User Input
        let userInput = `
            <div class="mt-3 mb-4 edit-doc-modal-form">
                <div class="my-2 error-block d-none" id="${this.clientId}-${typeName}-error">
                    <div>
                        <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                        <span class="error-text"></span>
                    </div>
                </div>
                <div>
                    <div>
                        <label>Collaborative Document Options</label>
                    </div>
                    <div class="d-flex collab-doc-info">
                        <div class="pr-2">
                            <input type="checkbox" class="m-0 collab-check" id="${this.clientId}-${typeName}" ${currentCollabCheck}>
                        </div>
                        <div class="pl-2">
                            <label class="font-weight-normal mb-0" for="${this.clientId}-${typeName}">Collaborative Document</label>
                        </div>
                    </div>
                    <div class="collab-text mt-2"> 
                        If the option above is selected, this document will be treated as a collaborative document. Collaborative Documents can be opened and edited by all other users with access to this page.
                    </div>
                </div>
                <div class="mt-3">
                    <button class="btn edit-doc-modal-btn" id="${this.clientId}-${typeName}-submit"><i class="fa-solid fa-file-signature pr-2"></i>Update Collaborative Options</div>
                </div>
            </div>
        `

        // Folder Structure Display
        let structureDisplay = `
            <div class="edit-doc-subheader mb-1">File Location</div>
            <div class="edit-doc-structure-container border" id="${this.clientId}-${typeName}-structure">
                ${this.generateDisplayStructure(data, typeName)}
            </div>
        `


        let html = `
            ${header}
            ${displayPath}
            <hr class="my-3">
            ${userInput}
            ${structureDisplay}
        `
        this.modalContentBlock.html(html);
        this.openModal();

        $(`#${this.clientId}-${typeName}-submit`).on('click', async () => {
            // Validate Form
            let isValid = await this.validateCollabDocUpdate();

            if (isValid) {
                // Updating Saving Text
                this.updateLoadingText("Updating Collaborative Options", "Collaborative Options Updated!");

                // Show Saving
                this.showSaving();

                let targetNodeId = data?.node?.original?.parent;

                // Submit
                let errorMessage = await this.submitCollabDocUpdate(data);
                if (errorMessage) {
                    let errorBlock = $(`#${this.clientId}-${typeName}-error`);
                    let errorText = errorBlock.find(".error-text");

                    errorText.text(errorMessage);
                    errorBlock.removeClass('d-none');

                    this.showSaving(false);
                    this.parentTree.refreshDocuments(targetNodeId);
                    return;
                }

                // Refresh Documents
                this.parentTree.refreshDocuments(targetNodeId);

                // Show Complete
                this.showSaved();

                // Close Modal
                setTimeout(() => {
                    this.modal.modal('hide');
                }, 1500);
            }
        })
    }
    private async validateCollabDocUpdate(): Promise<boolean> {
        let typeName = "collab-doc-update"

        let errorBlock = $(`#${this.clientId}-${typeName}-error`);
        errorBlock.addClass("d-none");
        let errorText = errorBlock.find(".error-text");

        let validSpToken = await this.validateSPToken();
        if (!validSpToken) {
            errorText.text("Please login to SharePoint to update this document.");
            errorBlock.removeClass('d-none');
            return false;
        }

        return true;
    }

    private async submitCollabDocUpdate(data: any) {
        let typeName = "collab-doc-update"
        let route = `${this.baseRoute}/collab-doc-update`

        let currentFilePath = data?.node?.original?.id;
        if (!currentFilePath) return;

        let filePathArr = currentFilePath.split("/");
        let currentFileName = filePathArr.pop();
        let currentFolderPath = filePathArr.join("/");

        if (!currentFileName) return;

        this.showDocTreeLoading("Updating Collaborative Options...");

        let isCollab = false;
        if (data?.node?.original?.displaySpLink == true) {
            isCollab = true;
        }

        let updateCollab = $(`#${this.clientId}-${typeName}`).is(":checked");
        if (updateCollab == isCollab) {
            return;
        }

        let requestBody = {
            fileName: currentFileName,
            folderPath: currentFolderPath,
            isCollab: isCollab,
            updateCollab: updateCollab
        }

        let result: any;
        try {
            result = await $.ajax(route, {
                method: "POST",
                headers: {
                    "ClientId": this.clientId
                },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    // Validate SP Token 
    private async validateSPToken(): Promise<boolean> {
        let result: string = await $.ajax(this.spAuthRoute, {
            method: "GET"
        });

        if (result) {
            window.open(result, "_blank", "popup");
            return false;
        }

        return true;
    }


    generateDisplayPath(data: any): string {
        let targetNode = data?.node?.original;
        if (!targetNode) return "";

        let pathIndicator = '<i class="fa-solid fa-angle-right file-path-indicator"></i>';

        let targetFilePathArr: string[] = (<string>targetNode.id).split("/")
        targetFilePathArr.pop();
        let targetFolderPath = targetFilePathArr.join("/");

        let pathString = `${this.treeLabel}/${targetFolderPath}/${targetNode.text}`;
        if (!targetFolderPath) {
            pathString = `${this.treeLabel}/${targetNode.text}`;
        }

        let pathHtml = "";
        let pathArr = pathString.split("/");
        
        if (pathString.length > 60 && pathArr.length > 2) {
            let firstItem = pathArr.shift();
            let lastItem = pathArr.pop();

            let dropdownHtml = "";
            pathArr.forEach(item => dropdownHtml += `<a class="dropdown-item">${item}</a>`);
            let overflowDisplay = `
                <div class="btn-group d-inline-block file-path-overflow">
                    <div class="dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="fa-solid fa-ellipsis"></i>
                    </div>
                    <div class="dropdown-menu">
                        ${dropdownHtml}
                    </div>
                </div>
            `

            let firstDisplayPath = `<span class='file-path-item'>${firstItem}</span>`
            let lastDisplayPath = `<span class='file-path-item'>${lastItem}</span>`

            pathHtml = `
                ${firstDisplayPath}
                ${pathIndicator}
                ${overflowDisplay}
                ${pathIndicator}
                ${lastDisplayPath}
            `;
        }
        else {
            let pathArrDisplay = pathArr.map(pathItem => `<span class='file-path-item'>${pathItem}</span>`);
            pathHtml = pathArrDisplay.join(pathIndicator);
        }

        let returnHtml = `
            <div class='edit-doc-display-path'>
                ${pathHtml}
            </div>
        `

        return returnHtml;
    }

    generateDisplayStructure(data: any, typeName: string, currentName: string = "", updatedFolderPath: string = undefined): string {
        if (typeName == "add-root-folder" || typeName == "add-root-file") {
            // Create Root Folder Node
            data = {
                node: {
                    original: {
                        id: "#"
                    }
                }
            }
        }

        let targetNode = data?.node?.original;
        if (!targetNode) return "";

        let targetFilePathArr: string[] = (<string>targetNode.id).split("/")
        targetFilePathArr.pop();
        let targetFolderPath = targetFilePathArr.join("/");

        let pathString = `${this.treeLabel}/${targetFolderPath}/${targetNode.text}`;
        if (!targetFolderPath) {
            pathString = `${this.treeLabel}/${targetNode.text}`;
        }

        //let pathString = `${this.treeLabel}/${targetNode.id}`;
        let fileExtension = "";
        let pathArr: string[] = pathString.split("/");
        if (typeName == "rename-file") {
            let filename = pathArr.pop();
            let fileArr = filename.split(".");
            let extension = fileArr.pop();
            fileExtension = `.${extension}`;
        }
        else if (typeName == "rename-folder" || typeName == "delete-folder" || typeName == "delete-file" || typeName == "add-root-folder" || typeName == "add-root-file" || typeName == "collab-doc-update") {
            pathArr.pop();

            if (typeName == "add-root-folder") typeName = "add-folder"
            else if (typeName == "add-root-file") typeName = "add-file"
        }
        else if (typeName == "move-folder" || typeName == "move-file") {
            if (!updatedFolderPath) {
                pathArr = [];
            }
            else {
                if (updatedFolderPath == "#") updatedFolderPath = this.treeLabel;
                else {
                    updatedFolderPath = `${this.treeLabel}/${updatedFolderPath}`;
                }
                pathArr = updatedFolderPath.split("/");
            }
        }
        let returnContent = "";

        for (let i = 0; i < pathArr.length; i++) {
            let displayName = pathArr[i];

            if (i == 0) {
                let content = `<div class="doc-structure-row"><i class="fa-solid fa-folder-tree edit-doc-structure-icon"></i><span class="pl-3">${displayName}</span></div>`;
                returnContent += content;
            }
            else {
                let margin = 10 + (30 * (i - 1));

                let content = `
                    <div style="margin-left: ${margin}px" class="doc-structure-row">
                        <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                        <i class="fa-solid fa-folder-open edit-doc-structure-icon pl-3"></i>
                        <span class="pl-2">${displayName}</span>
                    </div>
                `
                returnContent += content;
            }
        }

        // Add one to the end
        if (typeName == 'add-folder') {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = currentName;
            if (displayName.trim() == "") {
                displayName = `New Folder`
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon add-doc-icon">
                    <i class="fa-solid fa-folder-plus edit-doc-structure-icon"></i>
                    <span class="pl-2 font-weight-500">${displayName}</span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
            returnContent += content;
        }
        else if (typeName == "add-file") {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = currentName;
            if (displayName.trim() == "") {
                displayName = `New File`
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon add-doc-icon">
                    <i class="fa-solid fa-file-circle-plus edit-doc-structure-icon"></i>
                    <span class="pl-2 font-weight-500">${displayName}</span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
            returnContent += content;
        }
        else if (typeName == "rename-folder") {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = currentName;
            if (displayName.trim() == "") {
                displayName = `New Folder Name`
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon edit-doc-icon">
                    <i class="fa-solid fa-folder edit-doc-structure-icon"></i>
                    <span class="pl-2 font-weight-500">${displayName}</span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
            returnContent += content;
        }
        else if (typeName == "rename-file") {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = currentName
            if (displayName.trim() == "") {
                displayName = `New File Name`
            }
            displayName = displayName + fileExtension;

            let fileIcon = this.getFileIconById(-1);
            let fileTypeId = data?.node?.original?.type;
            if (fileTypeId) {
                fileIcon = this.getFileIconById(fileTypeId);
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon edit-doc-icon">
                    <i class="${fileIcon} edit-doc-structure-icon"></i>
                    <span class="pl-2 ffont-weight-500">${displayName}</span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
            returnContent += content;
        }
        else if (typeName == "delete-folder") {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = currentName;
            if (displayName.trim() == "") {
                displayName = `Folder Name`
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon delete-doc-icon">
                    <i class="fa-solid fa-folder edit-doc-structure-icon"></i>
                    <span class="pl-2 font-weight-500"><s>${displayName}</s></span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
            returnContent += content;
        }
        else if (typeName == "delete-file") {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = currentName
            if (displayName.trim() == "") {
                displayName = `File Name`
            }

            let fileIcon = this.getFileIconById(-1);
            let fileTypeId = data?.node?.original?.type;
            if (fileTypeId) {
                fileIcon = this.getFileIconById(fileTypeId);
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon delete-doc-icon">
                    <i class="${fileIcon} edit-doc-structure-icon"></i>
                    <span class="pl-2 font-weight-500"><s>${displayName}</s></span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
            returnContent += content;
        }
        else if (typeName == "move-folder") {
            if (!updatedFolderPath) {
                returnContent += `
                    <div class="doc-structure-row">
                        Please select a new folder location.
                    </div>
                `
            }
            else {
                let margin = 10 + (30 * (pathArr.length - 1));
                let displayName = data?.node?.original?.text;
                if (!displayName) displayName = "";
                if (displayName.trim() == "") {
                    displayName = `Folder Name`
                }

                let iconContent = `
                    <div class="ml-3 d-inline-block text-center edit-doc-circle-icon edit-doc-icon">
                        <i class="fa-solid fa-folder edit-doc-structure-icon"></i>
                        <span class="pl-2 font-weight-500">${displayName}</span>
                    </div>
                `

                let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
                returnContent += content;
            }
        }
        else if (typeName == "move-file") {
            if (!updatedFolderPath) {
                returnContent += `
                    <div class="doc-structure-row">
                        Please select a new folder location.
                    </div>
                `
            }
            else {
                let margin = 10 + (30 * (pathArr.length - 1));
                let displayName = data?.node?.original?.text;
                if (!displayName) displayName = "";
                if (displayName.trim() == "") {
                    displayName = `File Name`
                }

                let fileIcon = this.getFileIconById(-1);
                let fileTypeId = data?.node?.original?.type;
                if (fileTypeId) {
                    fileIcon = this.getFileIconById(fileTypeId);
                }

                let iconContent = `
                    <div class="ml-3 d-inline-block text-center edit-doc-circle-icon edit-doc-icon">
                        <i class="${fileIcon} edit-doc-structure-icon"></i>
                        <span class="pl-2 font-weight-500">${displayName}</span>
                    </div>
                `

                let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}
                    
                </div>
            `
                returnContent += content;
            }
        }
        else if (typeName == "collab-doc-update") {
            let margin = 10 + (30 * (pathArr.length - 1));
            let displayName = "";
            if (data?.node?.original?.text) {
                displayName = data?.node?.original?.text;
            }
            if (displayName.trim() == "") {
                displayName = `File Name`
            }

            let fileIcon = this.getFileIconById(-1);
            let fileTypeId = data?.node?.original?.type;
            if (fileTypeId) {
                fileIcon = this.getFileIconById(fileTypeId);
            }

            let iconContent = `
                <div class="ml-3 d-inline-block text-center edit-doc-circle-icon edit-doc-icon">
                    <i class="${fileIcon} edit-doc-structure-icon"></i>
                    <span class="pl-2 font-weight-500">${displayName}</span>
                </div>
            `

            let content = `
                <div style="margin-left: ${margin}px" class="doc-structure-row">
                    <i class="fa-solid fa-ellipsis-vertical edit-doc-structure-icon"></i>
                    ${iconContent}   
                </div>
            `
            returnContent += content;
        }
        

        return returnContent;
    }

    private drawFileGallery(data: any = null, typeName: string = "add-file"): void {
        let uploadGallery = this.modal.find(".file-upload-gallery");
        uploadGallery.empty();
        let galleryContent = "";

        // Add Content for Each File
        for (let i = 0; i < this.filesToUpload.length; i++) {
            let icon = this.getFileIcon(this.filesToUpload[i].type);

            let collabText = "";
            // Only give option to upload as collaborative if its a merge client
            if (this.parentTree.section.isMerged()) {
                if (this.isOfficeDocument(this.filesToUpload[i].name)) {
                    collabText = `
                        <hr class="my-2">
                        <div class="d-flex mt-1 collab-doc-info">
                            <div class="pr-2">
                                <input type="checkbox" class="m-0 collab-check" id="${this.clientId}-add-file-collab-check-${i}" data-filename="${this.filesToUpload[i].name}" checked>
                            </div>
                            <div class="pl-2">
                                <label for="${this.clientId}-add-file-collab-check-${i}">Upload as Collaborative Document?</label>
                            </div>
                        </div>
                        <div class="collab-text py-1"> 
                            Collaborative Documents can be opened and edited by all other users with access to this page.
                        </div>
                    `
                }
            }

            // Add Warning if new file will overwrite existing file
            let warningContent = "";
            let filePath = this.filesToUpload[i].name;
            if (data) {
                let parentId = data?.node?.original?.id;
                filePath = `${parentId}/${this.filesToUpload[i].name}`;
                if (parentId == "#") {
                    filePath = this.filesToUpload[i].name;
                }
            }
            let foundExistingFile = this.parentTree.allNodes.some(node => node.id.toLowerCase() == filePath.toLowerCase());
            if (foundExistingFile) {
                warningContent = `
                    <hr class="my-2">
                    <div class="warning-text d-flex">
                        <div class="pr-3 warning-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
                        <div>A file already exists in this location with the same file name. Uploading this file will overwrite the existing file.</div>
                    </div>
                `
            }


            galleryContent += `
                <div class="gallery-item">
                    <div class="col-12">
                        <div class="header-text">
                            <div class="text-decoration-none d-flex align-items-center">
                                ${icon}<span class="expand-text d-block pl-3">${this.filesToUpload[i].name}</span>
                                <span class="d-inline-block ml-auto add-file-delete-btn" data-index="${i}"><i class="fa-solid fa-xmark"></i></span>
                            </div>
                            ${warningContent}
                            ${collabText}
                        </div>
                    </div>
                </div>
            `
        }
        uploadGallery.append(galleryContent);

        uploadGallery.find(".add-file-delete-btn").on('click', (e) => {
            let target = $(e.currentTarget);
            let targetIndex: number = <number>target.data("index");
            this.filesToUpload.splice(targetIndex, 1);
            this.drawFileGallery(data);
            this.updateAddFileStructure(data, typeName)
        })

        if (this.filesToUpload.length > 0) {
            $(`#${this.clientId}-add-new-file-submit`).prop('disabled', false);
        }
        else {
            $(`#${this.clientId}-add-new-file-submit`).prop('disabled', true);
        }
    }

    private drawFolderSelect(data: any, typeName: string, folderNodes: DocumentNode[], currentParentFolderId: string, selectedFolderId: string = undefined): string {
        if (typeName != "move-folder" && typeName != "move-file") return;

        // Get Current Folder ID (can't move folder inside of itself)
        let currentItemFolderId = "";
        if (typeName == "move-folder") currentItemFolderId = data?.node?.original?.id;
        if (!currentItemFolderId) currentItemFolderId = "";

        // Get Parent Folder Id 
        let foundParentId = folderNodes.find(node => node.id == currentParentFolderId)?.id;
        if (!foundParentId) foundParentId = "#";

        // Get Path to Parent Folder
        let folderPath = foundParentId;
        if (folderPath == "#") {
            folderPath = this.treeLabel;
        }
        else {
            folderPath = `${this.treeLabel}/${folderPath}`;
        }

        // Interactive Folder Path
        let pathIndicator = '<i class="fa-solid fa-angle-right file-path-indicator"></i>';
        let pathHtml = "";
        let pathArr = folderPath.split("/");

        if (folderPath.length > 60 && pathArr.length > 2) {
            let firstItem = pathArr.shift();
            let lastItem = pathArr.pop();

            let dropdownHtml = "";
            let currentFolderPath = "";
            pathArr.forEach(item => {
                if (currentFolderPath != "") currentFolderPath += "/";
                currentFolderPath += item;
                dropdownHtml += `<a class="dropdown-item edit-doc-folder-select-item" data-folder-id="${currentFolderPath}">${item}</a>`
            });

            let overflowDisplay = `
                <div class="btn-group d-inline-block file-path-overflow">
                    <div class="dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="fa-solid fa-ellipsis"></i>
                    </div>
                    <div class="dropdown-menu">
                        ${dropdownHtml}
                    </div>
                </div>
            `
            let firstDisplayPath = `<span class='file-path-item edit-doc-folder-select-item' data-folder-id="#">${firstItem}</span>`
            let lastDisplayPath = `<span class='file-path-item edit-doc-folder-current-select'>${lastItem}</span>`

            pathHtml = `
                ${firstDisplayPath}
                ${pathIndicator}
                ${overflowDisplay}
                ${pathIndicator}
                ${lastDisplayPath}
            `;
        }
        else {
            let lastItem = pathArr.pop();
            // Check if we are in the root parent folder
            if (pathArr.length == 0) {
                lastItem = `<span class='file-path-item edit-doc-folder-current-select' data-folder-id="#">${lastItem}</span>`
            }
            else {
                lastItem = `<span class='file-path-item edit-doc-folder-current-select' data-folder-id="${lastItem}">${lastItem}</span>`
            }

            let pathArrDisplay = [];
            let currentFolderPath = "";
            for (let i = 0; i < pathArr.length; i++) {
                let pathItem = pathArr[i];             
                if (i == 0) {
                    pathArrDisplay.push(`<span class='file-path-item edit-doc-folder-select-item' data-folder-id="#">${pathItem}</span>`)
                }
                else {
                    if (currentFolderPath != "") currentFolderPath += "/";
                    currentFolderPath += pathItem;
                    pathArrDisplay.push(`<span class='file-path-item edit-doc-folder-select-item' data-folder-id="${currentFolderPath}">${pathItem}</span>`);
                }              
            }
            pathArrDisplay.push(lastItem);
            pathHtml = pathArrDisplay.join(pathIndicator);
        }

        // Folder Select Display
        let foundFolders = folderNodes.filter(node => node.parent == foundParentId && node.id != currentItemFolderId);
        let shownFolderRows = "";

        // Add "Root" option if in the root element
        if (foundParentId == "#") {
            let checked = "";
            if (selectedFolderId == "#") checked = "checked";

            let rootFolderRow = `
                <div class='edit-folder-row d-flex'>
                    <div class="pr-2">
                        <input type="checkbox" class="folder-check" id="${this.clientId}-${typeName}-folder-check" value="#" ${checked}>
                    </div>
                    <div class="pl-2" id="${this.clientId}-${typeName}-folder-select" data-folder-id="#">
                        <i class="fa-solid fa-folder-tree pr-2"></i>Root (${this.treeLabel})
                    </div>
                </div>
            `

            shownFolderRows += rootFolderRow;
        }
        foundFolders.forEach(folder => {
            let checked = "";
            if (selectedFolderId == folder.id) checked = "checked";

            let folderRow = `
                <div class='edit-folder-row d-flex'>
                    <div class="pr-2">
                        <input type="checkbox" class="folder-check" id="${this.clientId}-${typeName}-folder-check" value="${folder.id}" ${checked}>
                    </div>
                    <div class="pl-2 folder-select" id="${this.clientId}-${typeName}-folder-select" data-folder-id="${folder.id}">
                        <i class="fa-solid fa-folder folder-select-icon pr-2"></i>${folder.text}
                        <i class="fa-solid fa-angle-right file-path-indicator folder-select-arrow pl-3"></i>
                    </div>
                </div>
            `
            shownFolderRows += folderRow;
        });


        if (shownFolderRows == "") {
            shownFolderRows = "<div class='edit-folder-row'>No Folders Found</div>";
        }

        // Return HTML
        return `
            <label class="mb-1">Select New Folder Location</label>
            <div class='edit-doc-folder-path-select' id="${this.clientId}-${typeName}-folder-path-select">
                ${pathHtml}
            </div>
            <div class="edit-doc-folder-select border" id="${this.clientId}-${typeName}-folder-select">
                ${shownFolderRows}
            </div>
        `
    }

    private updateAddFileStructure(data: any, typeName: string) {
        // Update Folder Structure Display w/ new name
        if (this.filesToUpload.length == 1) {
            let targetFile = this.filesToUpload[0];
            let updatedStructure = this.generateDisplayStructure(data, typeName, targetFile.name);
            $(`#${this.clientId}-${typeName}-structure`).removeClass("d-none").prev().removeClass("d-none");
            $(`#${this.clientId}-${typeName}-structure`).html(updatedStructure);

            // enable submit btn
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', false);
        }
        else if (this.filesToUpload.length > 1) {
            $(`#${this.clientId}-${typeName}-structure`).addClass("d-none").prev().addClass("d-none"); // Hide structure for multiple files

            // enable submit btn
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', false);
        }
        else {
            let updatedStructure = this.generateDisplayStructure(data, typeName);
            $(`#${this.clientId}-${typeName}-structure`).html(updatedStructure);
            $(`#${this.clientId}-${typeName}-structure`).removeClass("d-none").prev().removeClass("d-none");
            // disabled submit btn
            $(`#${this.clientId}-${typeName}-submit`).prop('disabled', true);
        }
    }

    private getFileIcon(fileType: string) {

        if (fileType.includes("image")) return `<i class="fas fa-file-image"></i>`;
        if (fileType.includes("pdf")) return `<i class="fa-solid fa-file-pdf"></i>`;
        if (fileType.includes("word")) return `<i class="fa-solid fa-file-word"></i>`;
        if (fileType.includes("spreadsheet")) return `<i class="fa-solid fa-file-excel"></i>`;
        if (fileType.includes("presentation")) return `<i class="fa-solid fa-file-powerpoint"></i>`;
        if (fileType.includes("audio")) return `<i class="fa-solid fa-file-audio"></i>`;
        if (fileType.includes("video")) return `<i class="fa-solid fa-file-video"></i>`;

        return `<i class="fa-solid fa-file"></i>`
    }

    private getFileIconById(typeId: number) {
        switch (typeId) {
            case 1:
                return "fa-solid fa-file-pdf";
            case 2:
                return "fa-solid fa-file-word";
            case 3:
                return "fa-solid fa-file-excel";
            case 4:
                return "fa-solid fa-file-powerpoint";
            case 5:
                return "fa-solid fa-file-audio";
            case 6:
                return "fa-solid fa-file-video";
            case 10:
                return "fas fa-file-image";
            default:
                return "fa-solid fa-file"
        }
    }

    private isOfficeDocument(fileName: string): boolean {
        let isOffice = false;
        let fileArr = fileName.split(".");
        if (fileArr.length > 1) {
            let fileExtension = fileArr.pop();
            fileExtension = `.${fileExtension}`;
            switch (fileExtension) {
                case ".doc":
                case ".docx":
                case ".dotx":
                    isOffice = true;
                    break;
                case ".xls":
                case ".xlsx":
                    isOffice = true;
                    break;
                case ".ppt":
                case ".pptx":
                case ".potx":
                    isOffice = true;
                    break;
            }
        }
        return isOffice;
    }
    private updateLoadingText(savingText: string = "", savedText: string = "") {
        let savingTextBlock = this.modal.find(".edit-doc-saving-text");
        let savedTextBlock = this.modal.find(".edit-doc-success-text");

        // Update Saving Text
        if (savingText == "") {
            savingText = "Updating";
        }

        if (savedText == "") {
            savedText = "Updated!";
        }

        savingTextBlock.text(savingText);
        savedTextBlock.text(savedText);        
    }

    private showDocTreeLoading(savingText: string) {
        let docTreeLoading = `
            <div id="${this.clientId}-tree" class="jstree">
                <div class="edit-doc-tree-loading-container">
                    <div class="edit-doc-tree-loading-text">
                        <span class="loading-icon">
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </span>
                        <span class="loading-text">
                            ${savingText}
                        </span>
                    </div>
                </div>
            </div>
        `;

        let fileDisplayContainerId = `#file-display-container-${this.clientId}-tree`;
        $(fileDisplayContainerId).empty().html(docTreeLoading);

        // Hide Toolbar
        $(this.parentTree.toolbarId).addClass('d-none');
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

}